import { generatePath } from "react-router-dom";

import { flow } from "fp-ts/lib/function";

import { AbsoluteRoute } from "../../../core/routes";
import { type MyLensesGetOrganizationsResponse } from "../../../gql-generated/types";
import { type PreSubmissionInfoFragment } from "../../api/fragments";
import { type GetOrganizationsPartialResponse } from "../../models";

type SubmissionRoute =
    | AbsoluteRoute.SUBMIT_ACCOUNT_SELECT
    | AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT
    | AbsoluteRoute.SUBMIT_LENS_FOLDER_CREATE
    | AbsoluteRoute.CREATE_ORG_LENS_FOLDER;

const createDeriveLensSubmissionRoute =
    (
        { isSameUser, validationErrors = [] }: PreSubmissionInfoFragment,
        { organizationsList: organizations }: MyLensesGetOrganizationsResponse,
        { organizationsList: organizationsAvailableForLensSpaceCreation }: GetOrganizationsPartialResponse,
        submitUnderDifferentAccount = false,
    ) =>
    (): SubmissionRoute => {
        const hasExistingLensSpaces = organizations.some(
            (organization) => Array.isArray(organization.lensSpacesList) && organization.lensSpacesList.length > 0,
        );

        const hasOrganizationsAvailableForLensSpaceCreation = organizationsAvailableForLensSpaceCreation.length > 0;

        if (!isSameUser && !submitUnderDifferentAccount) {
            return AbsoluteRoute.SUBMIT_ACCOUNT_SELECT;
        }
        // Validation errors are fatal: pin the user to the folder-select page where
        // the alerts render and the form is disabled, regardless of org/folder state.
        // The destructure default above guards against stale Apollo cache loaded
        // before this field was added to the fragment.
        if (validationErrors.length > 0) {
            return AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT;
        }
        if (hasExistingLensSpaces) {
            return AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT;
        } else if (hasOrganizationsAvailableForLensSpaceCreation) {
            return AbsoluteRoute.SUBMIT_LENS_FOLDER_CREATE;
        } else {
            return AbsoluteRoute.CREATE_ORG_LENS_FOLDER;
        }
    };

const buildSubmissionRoute =
    (submissionId: string) =>
    (route: SubmissionRoute): string => {
        return generatePath(route, { submissionId, routeTo: "submit" });
    };

export const getLensSubmissionRouteV3 = (
    getPreSubmissionInfo: PreSubmissionInfoFragment,
    getMyLensesOrganizations: MyLensesGetOrganizationsResponse,
    getOrganizationsAvailableForLensSpaceCreation: GetOrganizationsPartialResponse,
    submissionId: string,
    submitUnderDifferentAccount = false,
): string => {
    return flow(
        createDeriveLensSubmissionRoute(
            getPreSubmissionInfo,
            getMyLensesOrganizations,
            getOrganizationsAvailableForLensSpaceCreation,
            submitUnderDifferentAccount,
        ),
        buildSubmissionRoute(submissionId),
    )();
};
